import type { SVGProps } from 'react';

/** Shared <defs> for a colour glow filter. Each instance uses a unique id. */
function GlowFilter({ id, color }: { id: string; color: string }) {
  return (
    <filter id={id} x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur stdDeviation="2.2" result="blur" />
      <feFlood floodColor={color} floodOpacity="0.85" />
      <feComposite in2="blur" operator="in" result="glow" />
      <feMerge>
        <feMergeNode in="glow" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
  );
}

// ————————————————————————— Flame (streak)
export function FlameIcon({
  animate = true,
  size = 28,
  ...rest
}: SVGProps<SVGSVGElement> & { animate?: boolean; size?: number }) {
  const id = 'flameGlow';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      aria-hidden
      {...rest}
    >
      <defs>
        <GlowFilter id={id} color="#F5A623" />
        <linearGradient id="flameGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFE08A" />
          <stop offset="60%" stopColor="#F5A623" />
          <stop offset="100%" stopColor="#FF5C5C" />
        </linearGradient>
      </defs>
      <g filter={`url(#${id})`} className={animate ? 'animate-flicker origin-bottom' : ''}>
        <path
          d="M16 3c2 4-3 6 1 11 1.3 1.6 3.4 1 3-1 2 3.2 4 6.5 4 10a8 8 0 0 1-16 0c0-4 2-6 3.4-8.5.9-1.6 2.2-.5 2.1 1C16.2 11.2 13.3 8 16 3z"
          fill="url(#flameGrad)"
        />
      </g>
    </svg>
  );
}

// ————————————————————————— Gem
export function GemIcon({
  size = 24,
  color = '#7B61FF',
  ...rest
}: SVGProps<SVGSVGElement> & { size?: number; color?: string }) {
  const id = `gemGlow-${color.slice(1)}`;
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden {...rest}>
      <defs>
        <GlowFilter id={id} color={color} />
        <linearGradient id="gemGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#C7BDFF" />
          <stop offset="100%" stopColor={color} />
        </linearGradient>
      </defs>
      <g filter={`url(#${id})`}>
        <path
          d="M8 5h16l6 8-14 14L2 13l6-8z"
          fill="url(#gemGrad)"
          stroke={color}
          strokeWidth="1.2"
          strokeLinejoin="round"
        />
        <path
          d="M8 5l4 8h8l4-8M2 13h28M12 13l4 14M20 13l-4 14"
          stroke="#F0EDE8"
          strokeOpacity="0.5"
          strokeWidth="0.7"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}

// ————————————————————————— Heart
export function HeartIcon({
  size = 22,
  full = true,
  ...rest
}: SVGProps<SVGSVGElement> & { size?: number; full?: boolean }) {
  const id = 'heartGlow';
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden {...rest}>
      <defs>
        <GlowFilter id={id} color="#FF5C5C" />
      </defs>
      <g filter={full ? `url(#${id})` : undefined}>
        <path
          d="M16 27s-10-6.3-10-13.2A5.8 5.8 0 0 1 16 9a5.8 5.8 0 0 1 10 4.8C26 20.7 16 27 16 27z"
          fill={full ? '#FF5C5C' : 'transparent'}
          stroke={full ? '#FF5C5C' : '#3a3a44'}
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}

// ————————————————————————— XP Bar (animated SVG rect)
interface XPBarProps {
  value: number;
  max: number;
  width?: number;
  height?: number;
  label?: string;
}

export function XPBar({ value, max, width = 180, height = 16, label }: XPBarProps) {
  const pct = Math.max(0, Math.min(1, value / max));
  const fillWidth = Math.max(6, pct * (width - 4));
  return (
    <svg width={width} height={height + 6} viewBox={`0 0 ${width} ${height + 6}`} aria-label={label}>
      <defs>
        <linearGradient id="xpGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#FFE08A" />
          <stop offset="100%" stopColor="#F5A623" />
        </linearGradient>
        <filter id="xpGlow" x="-10%" y="-20%" width="120%" height="140%">
          <feGaussianBlur stdDeviation="1.4" />
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <rect
        x="0"
        y="3"
        width={width}
        height={height}
        rx={height / 2}
        fill="#0D0F1C"
        stroke="#2A2E44"
        strokeWidth="1"
      />
      <rect
        x="2"
        y="5"
        width={fillWidth}
        height={height - 4}
        rx={(height - 4) / 2}
        fill="url(#xpGrad)"
        filter="url(#xpGlow)"
        style={{ transition: 'width 900ms cubic-bezier(0.2, 0.9, 0.3, 1)' }}
      />
    </svg>
  );
}

// ————————————————————————— Check / X feedback
export function CheckIcon({
  size = 64,
  color = '#3DFFA0',
  ...rest
}: SVGProps<SVGSVGElement> & { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" aria-hidden {...rest}>
      <defs>
        <filter id="chkGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2.6" />
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <circle cx="32" cy="32" r="28" stroke={color} strokeWidth="4" fill="transparent" filter="url(#chkGlow)" />
      <path
        d="M18 33l10 10 18-22"
        stroke={color}
        strokeWidth="6"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        filter="url(#chkGlow)"
      />
    </svg>
  );
}

export function CrossIcon({
  size = 64,
  color = '#FF5C5C',
  ...rest
}: SVGProps<SVGSVGElement> & { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" aria-hidden {...rest}>
      <defs>
        <filter id="xGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2.6" />
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <circle cx="32" cy="32" r="28" stroke={color} strokeWidth="4" fill="transparent" filter="url(#xGlow)" />
      <path
        d="M20 20l24 24M44 20L20 44"
        stroke={color}
        strokeWidth="6"
        strokeLinecap="round"
        fill="none"
        filter="url(#xGlow)"
      />
    </svg>
  );
}

// ————————————————————————— Badge (shimmer on unlock)
export function BadgeIcon({
  size = 96,
  shimmer = true,
  label = '',
  ...rest
}: SVGProps<SVGSVGElement> & { size?: number; shimmer?: boolean; label?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" aria-hidden {...rest}>
      <defs>
        <linearGradient id="badgeBody" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F5A623" />
          <stop offset="100%" stopColor="#CC8410" />
        </linearGradient>
        <linearGradient id="badgeShine" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0" />
          <stop offset="50%" stopColor="#FFFFFF" stopOpacity="0.75" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
          {shimmer && (
            <animate attributeName="x1" values="-1;1" dur="1.8s" repeatCount="indefinite" />
          )}
          {shimmer && (
            <animate attributeName="x2" values="0;2" dur="1.8s" repeatCount="indefinite" />
          )}
        </linearGradient>
        <filter id="badgeGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2.4" />
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <g filter="url(#badgeGlow)">
        <path
          d="M50 6l12 8 14-1 2 14 9 10-9 10-2 14-14-1-12 8-12-8-14 1-2-14-9-10 9-10 2-14 14 1L50 6z"
          fill="url(#badgeBody)"
          stroke="#8A5A08"
          strokeWidth="1"
        />
        <circle cx="50" cy="50" r="18" fill="#0D0F1C" stroke="#F5A623" strokeWidth="2" />
        <text
          x="50"
          y="56"
          textAnchor="middle"
          fill="#F5A623"
          fontSize="18"
          fontFamily="Boogaloo, 'Marker Felt', cursive"
        >
          {label || '★'}
        </text>
        {/* Shimmer overlay */}
        <rect x="10" y="20" width="80" height="60" fill="url(#badgeShine)" opacity="0.7" />
      </g>
    </svg>
  );
}

// ————————————————————————— Stage path (glowing amber SVG path)
interface StagePathProps {
  points: Array<{ x: number; y: number; status: 'done' | 'active' | 'locked' }>;
  width?: number;
  height?: number;
  onNodeClick?: (idx: number, status: 'done' | 'active' | 'locked') => void;
}

export function StagePath({ points, width = 320, height = 560, onNodeClick }: StagePathProps) {
  // Build a smoothed path across the points (Catmull-Rom-ish via quadratic).
  const d =
    points.length < 2
      ? ''
      : points
          .map((p, i) => {
            if (i === 0) return `M ${p.x} ${p.y}`;
            const prev = points[i - 1];
            const cx = (prev.x + p.x) / 2;
            const cy = (prev.y + p.y) / 2;
            return `Q ${prev.x} ${cy} ${cx} ${cy} T ${p.x} ${p.y}`;
          })
          .join(' ');

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} fill="none">
      <defs>
        <filter id="pathGlow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="3" />
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <radialGradient id="nodeActive" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="#FFE08A" />
          <stop offset="100%" stopColor="#F5A623" />
        </radialGradient>
      </defs>
      <path
        d={d}
        stroke="#F5A623"
        strokeOpacity="0.35"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray="4 10"
        filter="url(#pathGlow)"
      />
      {points.map((p, i) => {
        const fill =
          p.status === 'done'
            ? '#3DFFA0'
            : p.status === 'active'
              ? 'url(#nodeActive)'
              : '#2A2E44';
        const stroke =
          p.status === 'done'
            ? '#25B073'
            : p.status === 'active'
              ? '#F5A623'
              : '#3a3a44';
        return (
          <g
            key={i}
            transform={`translate(${p.x} ${p.y})`}
            onClick={() => onNodeClick?.(i, p.status)}
            className={p.status === 'locked' ? 'cursor-not-allowed' : 'cursor-pointer'}
            style={{ pointerEvents: 'auto' }}
          >
            <circle
              r="28"
              fill={fill}
              stroke={stroke}
              strokeWidth="3"
              filter={p.status === 'active' ? 'url(#pathGlow)' : undefined}
              className={p.status === 'active' ? 'animate-amber-pulse' : ''}
            />
            <text
              y="6"
              textAnchor="middle"
              fontFamily="Boogaloo, 'Marker Felt', cursive"
              fontSize="22"
              fill={p.status === 'locked' ? '#6A6A7A' : '#0D0F1C'}
            >
              {p.status === 'locked' ? '🔒' : i + 1}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ————————————————————————— Chevron / arrow
export function ChevronRight({ size = 18, ...rest }: SVGProps<SVGSVGElement> & { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden {...rest}>
      <path
        d="M9 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
